'use client';

import { forwardRef, useContext, useId, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { FieldSetContext } from '../field-set/context/field-set.context';
import type { FieldSetContextInterface } from '../field-set/types/field-set.types';
import { FormFieldContext } from '../form-field/context/form-field.context';
import type { FormFieldContextInterface } from '../form-field/types/form-field.types';
import { labeledControlRecipe } from './styles/labeled-control.styles';
import type { LabeledControlPropsInterface } from './types/labeled-control.types';

/**
 * @related ['Checkbox', 'Label', 'Radio', 'Switch']
 * @since 6.0.0
 */

export const LabeledControl = forwardRef<HTMLDivElement, LabeledControlPropsInterface>(
  (props: LabeledControlPropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);
    const fieldSetContext = useContext(FieldSetContext);

    const defaultId = useId();

    const {
      className,
      control,
      controlId = defaultId,
      css: cssProp,
      disabled = formFieldContext.disabled || false,
      error = formFieldContext.error || false,
      fluid = false,
      label,
      labelPosition = 'right',
      required = formFieldContext.required || false,
      optional = formFieldContext.optional || false,
      size = fieldSetContext.size || 'md',
      ...rest
    } = props;

    const styles = css(labeledControlRecipe.raw({ fluid, labelPosition, size }), cssProp);

    const formFieldContextValue = useMemo<FormFieldContextInterface>(
      () => ({
        controlId,
        error,
        disabled,
        optional,
        required,
      }),
      [controlId, error, disabled, optional, required],
    );

    const fieldSetContextValue = useMemo<FieldSetContextInterface>(
      () => ({
        size,
      }),
      [size],
    );

    return (
      <FormFieldContext.Provider value={formFieldContextValue}>
        <FieldSetContext.Provider value={fieldSetContextValue}>
          <div
            ref={forwardedRef}
            data-c11n-component="LabeledControl"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', styles, className)}
            {...rest}
          >
            {control}
            {label}
          </div>
        </FieldSetContext.Provider>
      </FormFieldContext.Provider>
    );
  },
);

LabeledControl.displayName = 'LabeledControl';
